/**
 * StepRequestsUser Component - User-Focused Version
 *
 * Shows tool calls in a user-friendly way:
 * - Tool explanation (what the LLM was trying to do and why)
 * - Tool name
 * - Result summary (not raw data)
 *
 * Uses the reactive GraphStore pattern:
 * - useNodes(step.model_requests) for reactive access to model requests
 * - No manual DAG reconstruction needed
 */

import React from 'react';
import type { Step } from '@sourcepilot/generated/chat/api/step';
import { ModelRequestResponseUser } from '@sourcepilot/ui/framework/ui/molecules/ModelRequestResponseUser';
import { useNodes } from '@sourcepilot/framework/relationship-manager';

interface StepRequestsUserProps {
  step: Step;
  isLastStep?: boolean;
}

export function StepRequestsUser({ step, isLastStep = false }: StepRequestsUserProps) {
  // REACTIVE: Get model requests from GraphStore using standalone hook
  // Hook is always called unconditionally (satisfies React Rules of Hooks)
  // Apply client-side ordering by order_index (memoized internally)
  const sortedModelRequests = useNodes(step.model_requests).orderBy('order_index').value();

  if (sortedModelRequests.length === 0) {
    return null; // No responses to render
  }

  return (
    <div className="flex flex-col space-y-3">
      {sortedModelRequests.map((modelRequest, index) => (
        <ModelRequestResponseUser
          key={modelRequest.id}
          modelRequest={modelRequest}
          // The activity / "stopped — Retry" indicator belongs only on the very
          // last response in the chat: the last request of the last step. A
          // buried incomplete response (e.g. a continuation abandoned mid-turn)
          // must not render a stray spinner or Retry prompt.
          isLastResponse={isLastStep && index === sortedModelRequests.length - 1}
        />
      ))}
    </div>
  );
}
